import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { HandCoins, FileSignature, Download, Plus, CheckCircle2, XCircle, Clock3, FileText, Printer } from 'lucide-react';
import { AOD_CLAUSES, printAod } from './aod';
import { SignaturePad } from '../common/SignaturePad';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_AMOUNT = 1000;

const AGREEMENT_TEXT =
  'I hereby request a short-term staff advance from JSM Transport and acknowledge that the full amount will be recovered as a single deduction from my next payslip. No interest is charged. I confirm the amount and reason below are correct, and my digital signature constitutes a valid acknowledgement of this agreement.';

const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * StaffLoanCard — driver / staff facing loan request + history card.
 * Canvas signature capture, single R1 000 max, single-outstanding rule.
 */
export default function StaffLoanCard() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState('1000');
  const [reason, setReason] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [idNumber, setIdNumber] = useState('');
  const [place, setPlace] = useState('');
  const [fullName, setFullName] = useState('');
  const [tplAvailable, setTplAvailable] = useState(false);
  const canvasRef = useRef(null);

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/staff-loans/my`, { headers });
      setLoans(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load loans');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplate = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/staff-loans/template/info`, { headers });
      setTplAvailable(!!r.data?.available);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchLoans(); fetchTemplate(); }, [fetchLoans, fetchTemplate]);
  useEffect(() => {
    if (!dlgOpen) return;
    axios.get(`${API_URL}/api/staff-loans/aod-defaults`, { headers })
      .then((r) => { setFullName(r.data.full_name || ''); setIdNumber((v) => v || r.data.id_number || ''); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dlgOpen]);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/staff-loans/template`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'Staff_Loan_Agreement_Template.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  const hasOutstanding = loans.some((l) => ['pending', 'approved'].includes(l.status));

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0 || amt > MAX_AMOUNT) return toast.error(`Amount must be R1 – R${MAX_AMOUNT}`);
    if (!reason.trim()) return toast.error('Reason is required');
    if (!/^[A-Za-z0-9]{6,20}$/.test(idNumber.replace(/\s+/g, ''))) return toast.error('Enter your ID / passport number');
    if (!place.trim()) return toast.error('Enter the place where you are signing');
    if (!agreed) return toast.error('You must accept the Acknowledgement of Debt');
    const c = canvasRef.current;
    if (!c || c.isBlank()) return toast.error('Please sign in the box');

    setSaving(true);
    try {
      const signature = c.toDataURL();
      await axios.post(`${API_URL}/api/staff-loans`, {
        amount: amt, reason: reason.trim(), signature, agreed: true, id_number: idNumber.replace(/\s+/g, ''), signed_place: place.trim(),
      }, { headers });
      toast.success('Loan request submitted — pending admin approval');
      setDlgOpen(false); setAmount('1000'); setReason(''); setAgreed(false);
      await fetchLoans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  const download = async (loan) => {
    try {
      const r = await axios.get(`${API_URL}/api/staff-loans/${loan.id}/agreement-pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Loan_Agreement_${loan.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const statusBadge = (s) => {
    if (s === 'approved')  return <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>;
    if (s === 'declined')  return <Badge className="bg-rose-600"><XCircle className="h-3 w-3 mr-1" /> Declined</Badge>;
    if (s === 'repaid')    return <Badge className="bg-slate-500">Repaid</Badge>;
    return <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50"><Clock3 className="h-3 w-3 mr-1" /> Pending</Badge>;
  };

  return (
    <Card data-testid="staff-loan-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <HandCoins className="h-4 w-4 text-emerald-600" /> Staff Loan (up to R1 000)
          </CardTitle>
          <CardDescription className="text-xs">
            Sign & submit a short-term advance. Deducted from next payslip once admin approves.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {tplAvailable && (
            <Button
              size="sm"
              variant="outline"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
              title="Download the loan agreement template"
            >
              <FileText className="h-4 w-4 mr-1" /> Template
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setDlgOpen(true)}
            disabled={hasOutstanding}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="request-loan-btn"
            title={hasOutstanding ? 'You already have an outstanding loan' : 'Request a loan'}
          >
            <Plus className="h-4 w-4 mr-1" /> Request Loan
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {hasOutstanding && (
          <div className="mb-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900 p-2">
            You have an outstanding loan — wait until it's repaid or declined before requesting another.
          </div>
        )}
        {loading ? (
          <div className="text-sm text-muted-foreground text-center py-4">Loading loans…</div>
        ) : loans.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-4">
            No loan requests yet.
          </div>
        ) : (
          <div className="space-y-2">
            {loans.map((l) => (
              <div key={l.id} className="p-3 border rounded flex items-center gap-3" data-testid={`loan-row-${l.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{R(l.amount)}</span>
                    {statusBadge(l.status)}
                    {l.review_note && <span className="text-[10px] text-slate-500 italic">"{l.review_note}"</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {l.reason || '—'} · requested {new Date(l.requested_at).toLocaleDateString('en-ZA')}
                    {l.approved_at && ` · approved ${new Date(l.approved_at).toLocaleDateString('en-ZA')}`}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => printAod(l.id)} data-testid={`print-aod-${l.id}`}>
                  <Printer className="h-3 w-3 mr-1" /> AOD
                </Button>
                <Button size="sm" variant="outline" onClick={() => download(l)} data-testid={`download-loan-${l.id}`}>
                  <Download className="h-3 w-3 mr-1" /> PDF
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Request dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="loan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-4 w-4" /> Acknowledgement of Debt — sign to submit
            </DialogTitle>
            <DialogDescription>
              Once approved, the amount is deducted from your next payslip in full. Maximum <b>R1 000</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount (R)</Label>
              <Input type="number" min="1" max={MAX_AMOUNT} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="loan-amount" />
            </div>
            <div>
              <Label className="text-xs">Reason for loan *</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. School fees advance" data-testid="loan-reason" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">ID / Passport No. *</Label>
                <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="13-digit ID" data-testid="loan-id-number" />
              </div>
              <div>
                <Label className="text-xs">Signed at (place) *</Label>
                <Input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="e.g. Cape Town" data-testid="loan-signed-place" />
              </div>
            </div>
            <div className="border rounded bg-slate-50 p-2 text-[11px] text-slate-700 leading-snug max-h-48 overflow-y-auto space-y-1" data-testid="loan-aod-text">
              <p className="font-bold text-slate-900 text-xs">ACKNOWLEDGEMENT OF DEBT</p>
              <p>
                I, <b>{fullName || '…'}</b>, ID <b>{idNumber || '…'}</b>, acknowledge that I am indebted to JSM Transport in the sum
                of <b>{R(amount)}</b> for: <i>{reason || '…'}</i>.
              </p>
              <ol className="list-decimal pl-4 space-y-0.5">{AOD_CLAUSES.map((c) => <li key={c}>{c}</li>)}</ol>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="loan-agree" />
              I have read and agree to this Acknowledgement of Debt.
            </label>
            <SignaturePad ref={canvasRef} testId="loan-signature" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="loan-submit">
              {saving ? 'Submitting…' : 'Sign & Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
